import type { QueryFilter, QueryOrderBy, FilterOperator } from '../types/query.types';

export interface ExploreQueryState {
  columns: string[];
  filters: QueryFilter[];
  orderBy: QueryOrderBy[];
  offset: number;
}

const OPERATORS: ReadonlySet<string> = new Set<FilterOperator>([
  '=', '!=', '<', '<=', '>', '>=', 'in', 'not in', 'like', 'ilike', 'is null', 'is not null', 'between',
]);

// Compact wire shape: short keys, empty fields omitted, to keep URLs short.
interface Wire {
  c?: string[];
  f?: QueryFilter[];
  o?: QueryOrderBy[];
  n?: number;
}

export function encodeQueryState(state: ExploreQueryState): string {
  const wire: Wire = {};
  if (state.columns.length) wire.c = state.columns;
  if (state.filters.length) wire.f = state.filters;
  if (state.orderBy.length) wire.o = state.orderBy;
  if (state.offset) wire.n = state.offset;
  return encodeURIComponent(JSON.stringify(wire));
}

export function decodeQueryState(raw: string | null | undefined): Partial<ExploreQueryState> | null {
  if (!raw) return null;
  let wire: unknown;
  try {
    wire = JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
  if (typeof wire !== 'object' || wire === null) return null;
  const w = wire as Wire;
  const out: Partial<ExploreQueryState> = {};

  if (Array.isArray(w.c)) out.columns = w.c.filter((x): x is string => typeof x === 'string');
  if (Array.isArray(w.f)) {
    out.filters = w.f.filter(
      (x): x is QueryFilter =>
        !!x && typeof x === 'object' && typeof (x as QueryFilter).column === 'string' && OPERATORS.has((x as QueryFilter).operator),
    );
  }
  if (Array.isArray(w.o)) {
    out.orderBy = w.o.filter(
      (x): x is QueryOrderBy => !!x && typeof x === 'object' && typeof (x as QueryOrderBy).column === 'string',
    );
  }
  if (typeof w.n === 'number' && Number.isFinite(w.n) && w.n >= 0) out.offset = w.n;

  return out;
}
